/**
 * `analyze(snapshot)` — top-level orchestrator for slack + critical path.
 *
 * Implements `05-` §A.7 `AnalysisResult` shape with D-13/D-14/D-15/D-16
 * semantics. Memoization wrapper lives in `core/api.ts`.
 *
 * Pipeline:
 *   1. `pickAnalysisConfig(snapshot.config)` — type-level enforcement of D-16
 *      lock-list.
 *   2. `buildAnalysisGraph(snapshot)` — memoized graph builder from Phase A.
 *   3. `findCycles(graph)` — strongly-connected components.
 *   4. Build the cycle-omit set from cycles (D-15).
 *   5. Compute the project-end anchor from `snapshot.projectStart`/`projectEnd`
 *      and `config.scheduleFromEnd`.
 *   6. `computeSlack(graph, config, projectEndTs, omitSet)` — D-13 CPM passes.
 *   7. `computeCriticalPath(slack, graph)` — D-13 threshold + D-12 completed.
 *   8. `connectedComponents` over the analysis graph for getConnectedGroup.
 *   9. Wrap into `AnalysisResult`.
 *
 * Day 1 scope: skeleton with stubs delegating to slack/critical_path/get_slack.
 * Day 5: full pipeline.
 */

import type {
  AnalysisResult,
  Snapshot,
  TaskID, NormalizedTaskID,
  Cycle
} from "../types";
import { normalizeTaskId } from "../ids";
import type { AnalysisGraph, SlackEntry } from "../graph/types";
import { buildAnalysisGraph, findCycles, connectedComponents } from "../api";
import { pickAnalysisConfig } from "../graph/analysis_graph";
import { computeSlack } from "./slack";
import { computeCriticalPath } from "./critical_path";
import { getSlackBetween as getSlackBetweenImpl } from "./get_slack";
import { memoize1 } from "../utils/memoize";

/**
 * Intermediate state shared by analyze() and getSlackBetween(). Pulled out
 * into a memoized helper so a single snapshot does at most one graph build +
 * slack computation, regardless of which public entry is called.
 */
export interface AnalysisInternals {
  readonly graph: AnalysisGraph;
  readonly slack: ReadonlyMap<NormalizedTaskID, SlackEntry>;
  readonly omitSet: ReadonlySet<NormalizedTaskID>;
  /**
   * Edges whose BOTH endpoints sit in the same multi-task SCC. Slack passes
   * filter these out so the rest of the (acyclic) graph still yields valid
   * earliest/latest dates for cycle-member tasks. Encoded as `source|target`
   * since one source-summary's expanded link may produce many AnalysisEdges
   * sharing a single `linkId` — a linkId-keyed set would over-omit (e.g.,
   * link `8 → projectA` expands to `8→2`, `8→3`, …, `8→9`; only the leaf
   * pair involved in a cycle should be filtered, not the rest).
   */
  readonly omitEdgePairs: ReadonlySet<string>;
  readonly cycles: readonly Cycle[];
}

function edgePairKey(source: NormalizedTaskID, target: NormalizedTaskID): string {
  return source + "|" + target;
}

function computeAnalysisInternals_unmemoized(snapshot: Snapshot): AnalysisInternals {
  const config = pickAnalysisConfig(snapshot.config);
  const graph = buildAnalysisGraph(snapshot);
  const cycles: readonly Cycle[] = findCycles(graph);

  // v1 parity (`ext/critical_path/slack.js:18-37`): the slack pipeline breaks
  // cycles by removing the cycle-forming LINKS, not by omitting the cycle
  // member TASKS — so non-cycle paths through those tasks still produce
  // useful slack. We mirror that: only SELF-LOOP cycles (size 1, no other
  // edges to analyze on) fall into `omitSet`; for multi-task SCCs, every
  // edge whose BOTH endpoints sit in the SCC's task set is suppressed via
  // `omitEdgePairs`. Test `retest_migrations/critical_path` "detects the
  // expected critical tasks with cyclic extra links" depends on task 11
  // (a cycle member) still appearing in the slack / critical maps via its
  // non-cycle predecessors.
  //
  // Each task belongs to at most one SCC, so building a task → SCC-index
  // map lets us answer "is this edge cycle-internal" in O(1) without
  // scanning all cycles.
  const omitSet = new Set<NormalizedTaskID>();
  const omitEdgePairs = new Set<string>();
  const taskToScc = new Map<NormalizedTaskID, number>();
  cycles.forEach((c, i) => {
    if (c.tasks.length === 1) {
      omitSet.add(c.tasks[0]);
    } else {
      for (const t of c.tasks) taskToScc.set(t, i);
    }
  });
  if (taskToScc.size > 0) {
    for (const e of graph.edges) {
      const sScc = taskToScc.get(e.source);
      if (sScc === undefined) continue;
      const tScc = taskToScc.get(e.target);
      if (sScc !== tScc) continue;
      omitEdgePairs.add(edgePairKey(e.source, e.target));
    }
  }

  const projectEndTs = config.scheduleFromEnd
    ? snapshot.projectStart.getTime()
    : snapshot.projectEnd.getTime();

  const slack = computeSlack(graph, config, projectEndTs, omitSet, omitEdgePairs);
  return { graph, slack, omitSet, omitEdgePairs, cycles };
}

/**
 * Memoized intermediate-state accessor. Shared by analyze() and
 * getSlackBetween() inside this module so they don't duplicate work for the
 * same snapshot.
 */
const _internals = memoize1(computeAnalysisInternals_unmemoized);

/**
 * Top-level analyze orchestrator (Day 5 — full pipeline).
 *
 * Steps per `05-` §A.7 + D-13/D-14/D-15/D-16:
 *   1. Narrow config via pickAnalysisConfig (D-16 type-level enforcement).
 *   2. Build analysis graph (memoized).
 *   3. Detect cycles → omit set (D-15).
 *   4. Resolve project-end horizon per D-23.
 *   5. computeSlack (forward + latest + summary aggregation + D-12 zero-slack).
 *   6. computeCriticalPath (threshold + D-12 override).
 *   7. connectedComponents over undirected projection (drives getConnectedGroup).
 *   8. Wrap into AnalysisResult.
 */
export function analyze_unmemoized(snapshot: Snapshot): AnalysisResult {
  const { graph, slack, cycles } = _internals(snapshot);
  const isCriticalMap = computeCriticalPath(slack, graph);

  // Build the public output maps from SlackEntry.
  const totalSlack = new Map<NormalizedTaskID, number>();
  const freeSlack = new Map<NormalizedTaskID, number>();
  for (const [id, entry] of slack) {
    totalSlack.set(id, entry.totalSlack);
    freeSlack.set(id, entry.freeSlack);
  }

  // Connected components over the undirected analysis graph projection.
  const connectedGroups = connectedComponents(graph);

  return {
    totalSlack,
    freeSlack,
    isCritical: isCriticalMap,
    connectedGroups,
    cycles
  };
}

/**
 * `getSlack(task1, task2)` — engine entry point per D-14. Returns slack
 * in minutes (engine standard unit per D-35). `Infinity` when no path
 * connects t1 and t2 in either direction; adapter is responsible for
 * mapping that to v1's date-arithmetic sentinel.
 */
export function getSlackBetween_unmemoized(
  snapshot: Snapshot,
  t1: TaskID,
  t2: TaskID
): number {
  const { graph, slack, omitSet, omitEdgePairs } = _internals(snapshot);
  return getSlackBetweenImpl(graph, slack, normalizeTaskId(t1), normalizeTaskId(t2), omitSet, omitEdgePairs);
}
